/**
 * Lite vs Pro feature gating (client-side).
 *
 * The server resolves a user into one of five effective tiers:
 *   'free' | 'lite' | 'pro' | 'lifetime_lite' | 'lifetime_pro'
 *
 * Client-side feature gating only cares about the PDF-tool surface, which is
 * identical between subscription Lite/Pro and lifetime Lite/Pro. So we
 * collapse the five into three for `currentTier`: free | lite | pro
 * (with 'trial' as the 7-day desktop trial state).
 *
 * Cloud and AI features have their own server-side gates — the client doesn't
 * need to mirror them in `currentTier`.
 */

const LITE_FEATURES = [
  'viewer',
  'merge',
  'split',
  'compress',
  'sign',
  'protect',
  'convert-image',
  'form-fill',
  'annotations-basic', // highlight + freehand draw only
];

const PRO_FEATURES = [
  ...LITE_FEATURES,
  'edit-mode',
  'annotations-full', // all annotation tools
  'invoice',
  'batch',
  'watermark',
  'crop',
  'page-numbers',
  'redact',
  'flatten',
  'ocr',
  'convert-full', // Word/Excel/PPT (desktop only)
  'esign',
];

let currentTier = 'free'; // Safe default; server (/account, /trial/status) sets the real tier

// Map the server's 5-tier model back to the 3-tier client gating model.
// Anything we don't recognize falls through to 'free'.
function normalizeTier(tier) {
  if (!tier) return 'free';
  if (tier === 'pro' || tier === 'lifetime_pro') return 'pro';
  if (tier === 'lite' || tier === 'lifetime_lite') return 'lite';
  if (tier === 'trial') return 'trial';
  return 'free';
}

export function setTier(tier) {
  currentTier = normalizeTier(tier);
}

export function getTier() {
  // Allow test override via window global (dev only)
  if (typeof window !== 'undefined' && window.__FORCE_TIER) {
    return window.__FORCE_TIER;
  }
  return currentTier;
}

/**
 * Check if the current tier has access to a feature.
 */
export function hasFeature(feature) {
  const tier = getTier();
  if (tier === 'pro') return PRO_FEATURES.includes(feature);
  // Trial: Pro features for 7 days, then degrade to Lite
  if (tier === 'trial' && getTrialDaysRemaining() > 0) {
    return PRO_FEATURES.includes(feature);
  }
  return LITE_FEATURES.includes(feature);
}

/**
 * Check if a feature is Pro-only (not in Lite).
 */
export function isProFeature(feature) {
  return !LITE_FEATURES.includes(feature);
}

/**
 * Trial mode helpers.
 */
export function isTrial() {
  return getTier() === 'trial';
}

const TRIAL_STARTED_KEY = 'scrivar_trial_started_at';

/**
 * Set the server-authoritative trial start (ISO string) and cache it for
 * offline launches. Pass null to clear (paid users). This REPLACES the old
 * local install-date — clearing localStorage no longer resets the clock,
 * because the next online /trial/status re-supplies the same start.
 */
export function setTrialStartedAt(iso) {
  try {
    if (iso) localStorage.setItem(TRIAL_STARTED_KEY, iso);
    else localStorage.removeItem(TRIAL_STARTED_KEY);
  } catch { /* ignore */ }
}

export function getTrialDaysRemaining() {
  let iso = null;
  try { iso = localStorage.getItem(TRIAL_STARTED_KEY); } catch { iso = null; }
  if (!iso) return null;
  const start = new Date(iso).getTime();
  if (Number.isNaN(start)) return null;
  const daysUsed = Math.floor((Date.now() - start) / (1000 * 60 * 60 * 24));
  return Math.max(0, 7 - daysUsed);
}

export function shouldWatermarkExport() {
  return isTrial() && getTrialDaysRemaining() === 0;
}
